import {
  FloatingFocusManager,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import { cx } from "@jugl-web/utils";
import { FC, ReactNode, RefObject } from "react";

type ChatBotDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  initialFocusRef?: RefObject<HTMLElement | null>;
  panelClassName?: string;
};

export const ChatBotDrawer: FC<ChatBotDrawerProps> = ({
  isOpen,
  onClose,
  children,
  initialFocusRef,
  panelClassName,
}) => {
  const { refs, context } = useFloating({
    strategy: "absolute",
    open: isOpen,
    onOpenChange: (nextOpen) => {
      if (!nextOpen) {
        onClose();
      }
    },
  });

  const dismiss = useDismiss(context, {
    outsidePress: true,
    escapeKey: true,
  });

  const role = useRole(context, {
    role: "dialog",
  });

  const { getFloatingProps } = useInteractions([dismiss, role]);

  const { isMounted, styles: backdropStyles } = useTransitionStyles(context, {
    duration: 200,
    initial: {
      opacity: 0,
    },
    open: {
      opacity: 1,
    },
    close: {
      opacity: 0,
    },
  });

  const { styles: panelTransitionStyles } = useTransitionStyles(context, {
    duration: 200,
    initial: {
      transform: "translateX(100%)",
    },
    open: {
      transform: "translateX(0%)",
    },
    close: {
      transform: "translateX(100%)",
    },
  });

  if (!isMounted) {
    return null;
  }

  return (
    <div className="absolute inset-0 z-[100] overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-auto absolute inset-0 bg-[#00000050] backdrop-blur-[3px]"
        style={backdropStyles}
      />
      <FloatingFocusManager
        context={context}
        modal
        guards
        initialFocus={initialFocusRef ?? refs.floating}
      >
        <div
          ref={refs.setFloating}
          className="absolute inset-y-0 right-0 z-10 flex w-full max-w-full overflow-hidden"
          {...getFloatingProps()}
        >
          <div
            style={panelTransitionStyles}
            className={cx(
              "h-full w-full max-w-full bg-white outline-none",
              panelClassName
            )}
          >
            {children}
          </div>
        </div>
      </FloatingFocusManager>
    </div>
  );
};
